<template>
  <!-- Creation Loading View -->
  <div
    v-if="isCreating"
    class="w-full h-auto mt-6 pb-20 bg-gray-50 dark:bg-background rounded-t-[20px] flex flex-col lg:flex-row items-start md:px-8 gap-6 lg:gap-8"
  >
    <div class="flex flex-col justify-between">
      <div class="w-full md:w-32 flex-shrink-0">
        <BackButton />
      </div>
      <!-- Form Summary Sidebar -->
      <div
        class="relative w-full lg:w-[360px] flex-shrink-0 lg:rounded-[20px] border-l lg:border border-border min-h-screen lg:min-h-[800px] transition-all duration-300 ease-in-out bg-card dark:bg-card mt-0 lg:mt-6 lg:ml-1 p-6 overflow-y-auto"
      >
        <div class="sticky top-6">
          <div class="flex items-center justify-between mb-8">
            <h2 class="text-[22px] font-semibold text-foreground">
              Creating Learning Goal
            </h2>
          </div>

          <div class="space-y-5 flex-1 overflow-y-auto pb-8 px-1">
            <!-- Topic Display -->
            <div
              class="space-y-1 p-2 rounded-[20px] bg-muted/30 border border-transparent"
            >
              <label
                class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
              >
                What do you want to learn today?
              </label>
              <div class="text-[18px] font-bold text-foreground">
                {{ formData.topic }}
              </div>
            </div>

            <!-- Experience Display -->
            <div
              class="space-y-1 p-2 rounded-[20px] bg-muted/30 border border-transparent"
            >
              <label
                class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
              >
                What's your experience level?
              </label>
              <div class="text-[18px] font-bold text-foreground">
                {{ formData.target_skill_level }}
              </div>
            </div>

            <!-- Time Display -->
            <div
              class="space-y-1 p-2 rounded-[20px] bg-muted/30 border border-transparent"
            >
              <label
                class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
              >
                How much time do you have to learn?
              </label>
              <div class="text-[18px] font-bold text-foreground">
                {{ formData.estimated_duration_weeks }} weeks
              </div>
            </div>

            <!-- Mentor Display -->
            <div
              class="space-y-1 p-2 rounded-[20px] bg-muted/30 border border-transparent"
            >
              <label
                class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
              >
                Selected Mentor
              </label>
              <div v-if="selectedMentor" class="flex items-center gap-2">
                <img
                  v-if="selectedMentor.profile_picture"
                  :src="selectedMentor.profile_picture"
                  alt="Mentor"
                  class="w-8 h-8 rounded-full"
                />
                <div class="text-[16px] font-semibold text-foreground">
                  {{ selectedMentor.first_name }} {{ selectedMentor.last_name }}
                </div>
              </div>
              <div v-else class="text-[16px] font-semibold text-foreground">
                None
              </div>
            </div>
            <!-- Workspace Display -->
            <div
              class="space-y-1 p-2 rounded-[20px] bg-muted/30 border border-transparent"
            >
              <label
                class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
              >
                Selected Workspace
              </label>
              <div v-if="selectedWorkspaceData" class="flex items-center gap-2">
                <div class="h-2 w-2 rounded-full bg-[#43CE33]"></div>
                <div class="text-[18px] font-bold text-foreground">
                  {{ selectedWorkspaceData.name }}
                </div>
              </div>
              <div v-else class="text-[16px] font-semibold text-foreground">
                None
              </div>
            </div>
            <!-- Learning Resources Display -->
            <div
              class="space-y-1 p-2 rounded-[20px] bg-muted/30 border border-transparent"
            >
              <label
                class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
              >
                Learning Resources
              </label>
              <div v-if="formData.resource_type === 'personal'" class="space-y-2">
                <div class="text-[17px] font-bold text-foreground">Personal</div>
                <div v-if="formData.personal_resources.length > 0" class="space-y-1 mt-1">
                  <a 
                    v-for="res in formData.personal_resources" 
                    :key="res.id"
                    :href="res.url"
                    target="_blank"
                    class="flex items-center gap-2 text-[14px] text-blue-500 hover:underline"
                  >
                    <Paperclip class="w-3.5 h-3.5" />
                    <span class="truncate max-w-[200px]">{{ res.filename }}</span>
                  </a>
                </div>
              </div>
              <div v-else-if="formData.resource_type === 'verified'" class="text-[18px] font-bold text-foreground">
                High-Quality
              </div>
              <div v-else class="text-[18px] font-bold text-foreground">
                Free (Online)
              </div>
            </div>


            <!-- Additional Information Display -->
            <div
              v-if="formData.additional_information"
              class="space-y-1 p-2 rounded-[20px] bg-muted/30 border border-transparent"
            >
              <label
                class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
              >
                Additional Information
              </label>
              <div
                class="text-[16px] font-semibold text-foreground break-words"
              >
                {{ formData.additional_information }}
              </div>
            </div>

            <!-- Creation Message -->
            <div
              class="mt-8 p-4 bg-muted/50 dark:bg-muted/30 rounded-xl border border-border"
            >
              <div class="flex items-start gap-3">
                <div
                  class="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-foreground mt-0.5"
                ></div>
                <div class="flex-1">
                  <p class="text-[15px] font-semibold text-foreground mb-1">
                    Generating learning plan...
                  </p>
                  <p class="text-[13px] text-muted-foreground">
                    This may take a while. We're creating a personalized
                    learning path just for you.
                  </p>
                </div>
              </div>
            </div>

            <!-- Cancel Button -->
            <button
              type="button"
              @click="cancelCreation"
              class="w-full mt-6 py-3 px-4 bg-transparent border border-border text-foreground rounded-full hover:bg-muted transition-all font-semibold text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content with Skeleton Loaders -->
    <div class="flex-1 w-full mt-6 lg:mt-0">
      <div
        class="bg-card dark:bg-card rounded-[20px] border border-border p-6 lg:p-8"
      >
        <!-- Skeleton Header -->
        <div class="mb-8">
          <div class="h-10 bg-muted rounded-lg w-1/3 mb-4 animate-pulse"></div>
          <div class="h-4 bg-muted rounded w-2/3 animate-pulse"></div>
        </div>

        <!-- Skeleton Tabs -->
        <div class="flex gap-4 mb-8">
          <div class="h-10 bg-muted rounded-full w-24 animate-pulse"></div>
          <div class="h-10 bg-muted rounded-full w-24 animate-pulse"></div>
          <div class="h-10 bg-muted rounded-full w-24 animate-pulse"></div>
        </div>

        <!-- Skeleton Content Blocks -->
        <div class="space-y-6">
          <div class="space-y-3">
            <div class="h-6 bg-muted rounded w-1/4 animate-pulse"></div>
            <div class="h-32 bg-muted rounded-lg animate-pulse"></div>
          </div>
          <div class="space-y-3">
            <div class="h-6 bg-muted rounded w-1/3 animate-pulse"></div>
            <div class="space-y-2">
              <div class="h-4 bg-muted rounded w-full animate-pulse"></div>
              <div class="h-4 bg-muted rounded w-5/6 animate-pulse"></div>
              <div class="h-4 bg-muted rounded w-4/6 animate-pulse"></div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="h-6 bg-muted rounded w-1/4 animate-pulse"></div>
            <div class="h-24 bg-muted rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Regular Create Form -->
  <div
    v-else
    class="relative w-full flex flex-col items-center px-4 min-h-[calc(100vh-4rem)] bg-gray-50 dark:bg-background overflow-y-auto"
  >
    <!-- Header row for Back button -->
    <div class="w-full py-4 flex items-center shrink-0">
      <BackButton />
    </div>

    <!-- Step Content -->
    <div class="w-full max-w-2xl flex-1 flex flex-col justify-center pb-20">
      <!-- Step 1: What would you like to learn? -->
      <div v-if="currentStep === 0" class="space-y-8">
        <h1
          class="text-2xl md:text-3xl font-semibold text-foreground dark:text-white text-center"
        >
          What would you like to learn?
        </h1>

        <div class="relative">
          <input
            v-model="formData.topic"
            type="text"
            placeholder="Describe it in your own words"
            class="w-full pl-6 pr-32 py-4 text-base border border-input dark:border-foreground/10 rounded-full focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
            @keyup.enter="nextStep"
          />
          <div
            class="absolute right-4 top-1/2 transform -translate-y-1/2 flex gap-2"
          >
            <button
              @click="goBack"
              class="group w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors text-white"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.73528 17.2206L0.25 8.73535M0.25 8.73535L8.73528 0.25007M0.25 8.73535H17.2206"
                  stroke="currentColor"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              @click="nextStep"
              :disabled="!canProceed"
              class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.48347 1.00007L17.9688 9.48535M17.9688 9.48535L9.48347 17.9706M17.9688 9.48535H0.998187"
                  stroke="currentColor"
                  :stroke-width="canProceed ? 2 : 0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: Experience Level -->
      <div v-if="currentStep === 1" class="space-y-8">
        <div
          v-if="currentStep > 0"
          class="space-y-2 md:space-y-3 text-[13px] md:text-sm text-muted-foreground mb-6 md:mb-8 text-left w-full"
        >
          <div>
            <p class="font-semibold text-foreground">
              What do you want to learn today?
            </p>
            <p class="text-muted-foreground">{{ formData.topic }}</p>
          </div>
        </div>

        <h1
          class="text-2xl md:text-3xl font-semibold text-foreground dark:text-white text-center"
        >
          What's your experience level?
        </h1>

        <div class="flex flex-col items-center space-y-6">
          <div class="flex flex-wrap justify-center gap-3">
            <button
              v-for="level in skillLevels"
              :key="level.value"
              @click="selectSkillLevel(level.value)"
              :class="[
                'px-6 py-3 rounded-full text-base font-medium transition-colors border',
                formData.target_skill_level === level.value
                  ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                  : 'bg-card border-input text-muted-foreground hover:bg-muted dark:border-border',
              ]"
            >
              {{ level.title }}
            </button>
          </div>

          <div class="relative w-full max-w-xl">
            <input
              v-model="customSkillLevel"
              type="text"
              placeholder="Or enter your answer here"
              class="w-full pl-6 pr-32 py-4 text-base border border-input dark:border-foreground/10 rounded-full focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
              @keyup.enter="nextStep"
            />
            <div
              class="absolute right-4 top-1/2 transform -translate-y-1/2 flex gap-2"
            >
              <button
                @click="previousStep"
                class="group w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors text-white"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.73528 17.2206L0.25 8.73535M0.25 8.73535L8.73528 0.25007M0.25 8.73535H17.2206"
                    stroke="currentColor"
                    stroke-width="0.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <button
                @click="nextStep"
                :disabled="!canProceed"
                class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white"
              >
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 19 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.48347 1.00007L17.9688 9.48535M17.9688 9.48535L9.48347 17.9706M17.9688 9.48535H0.998187"
                    stroke="currentColor"
                    :stroke-width="canProceed ? 2 : 0.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Duration -->
      <div v-if="currentStep === 2" class="space-y-8">
        <div
          class="space-y-2 md:space-y-3 text-[13px] md:text-sm text-muted-foreground mb-6 md:mb-8 text-left w-full"
        >
          <div>
            <p class="font-semibold text-foreground">
              What do you want to learn today?
            </p>
            <p class="text-muted-foreground">{{ formData.topic }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              What is your experience level?
            </p>
            <p class="text-muted-foreground">
              {{ getSkillLevelTitle(formData.target_skill_level) }}
            </p>
          </div>
        </div>

        <h1
          class="text-2xl md:text-3xl font-semibold text-foreground dark:text-white text-center"
        >
          How much time do you have to learn?
        </h1>

        <div class="flex flex-col items-center space-y-6">
          <div class="flex flex-wrap justify-center gap-3">
            <button
              v-for="duration in durationOptions"
              :key="duration.value"
              @click="selectDuration(duration.value)"
              :class="[
                'px-6 py-3 rounded-full text-base font-medium transition-colors border',
                formData.estimated_duration_weeks === duration.value
                  ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                  : 'bg-card border-input text-muted-foreground hover:bg-muted dark:border-border',
              ]"
            >
              {{ duration.title }}
            </button>
          </div>

          <div class="relative w-full max-w-xl">
            <input
              v-model.number="customDuration"
              type="text"
              placeholder="Type or select an option"
              class="w-full pl-6 pr-32 py-4 text-base border border-input dark:border-foreground/10 rounded-full focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
              @keyup.enter="selectCustomDuration"
            />
            <div
              class="absolute right-4 top-1/2 transform -translate-y-1/2 flex gap-2"
            >
              <button
                @click="previousStep"
                class="group w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors text-white"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.73528 17.2206L0.25 8.73535M0.25 8.73535L8.73528 0.25007M0.25 8.73535H17.2206"
                    stroke="currentColor"
                    stroke-width="0.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <button
                @click="nextStep"
                :disabled="!canProceed"
                class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white"
              >
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 19 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.48347 1.00007L17.9688 9.48535M17.9688 9.48535L9.48347 17.9706M17.9688 9.48535H0.998187"
                    stroke="currentColor"
                    :stroke-width="canProceed ? 2 : 0.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 5: Mentor Selection -->
      <div v-if="currentStep === 4" class="space-y-8">
        <div
          class="space-y-2 md:space-y-3 text-[13px] md:text-sm text-muted-foreground mb-6 md:mb-8 text-left w-full"
        >
          <div>
            <p class="font-semibold text-foreground">
              What do you want to learn today?
            </p>
            <p class="text-muted-foreground">{{ formData.topic }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              What is your experience level?
            </p>
            <p class="text-muted-foreground">
              {{ getSkillLevelTitle(formData.target_skill_level) }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              How much time do you have to learn?
            </p>
            <p class="text-muted-foreground">{{ getDurationText() }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Learning Style</p>
            <p class="text-muted-foreground">
              {{ getLearningStyleTitle(formData.learning_style) }}
            </p>
          </div>
        </div>

        <h1
          class="text-2xl md:text-3xl font-semibold text-foreground dark:text-white text-center"
        >
          Do you want to add a human mentor?
        </h1>

        <div class="flex flex-col items-center space-y-6">
          <div class="flex flex-wrap justify-center gap-3">
            <button
              @click="selectMentorOption('not_yet')"
              :class="[
                'px-6 py-3 rounded-full text-base font-medium transition-colors border',
                mentorOption === 'not_yet'
                  ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                  : 'bg-card border-input text-muted-foreground hover:bg-muted dark:border-border',
              ]"
            >
              Not yet
            </button>
            <button
              @click="selectMentorOption('yes')"
              :class="[
                'px-6 py-3 rounded-full text-base font-medium transition-colors border',
                mentorOption === 'yes'
                  ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                  : 'bg-card border-input text-muted-foreground hover:bg-muted dark:border-border',
              ]"
            >
              Yes, choose a mentor
            </button>
          </div>

          <div class="relative w-full max-w-[960px]">
            <input
              v-model="mentorSearchQuery"
              type="text"
              placeholder="Search for a mentor by name or skills"
              :disabled="mentorOption !== 'yes'"
              class="w-full pl-6 pr-32 py-4 text-base border border-input dark:border-foreground/10 rounded-full focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground disabled:bg-muted disabled:cursor-not-allowed"
              @input="handleMentorSearch"
            />
            <div
              class="absolute right-4 top-1/2 transform -translate-y-1/2 flex gap-2"
            >
              <button
                @click="previousStep"
                class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.73528 17.2206L0.25 8.73535M0.25 8.73535L8.73528 0.25007M0.25 8.73535H17.2206"
                    stroke="currentColor"
                    stroke-width="0.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <button
                @click="nextStep"
                class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors text-white"
              >
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 19 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.48347 1.00007L17.9688 9.48535M17.9688 9.48535L9.48347 17.9706M17.9688 9.48535H0.998187"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Mentor Results -->
          <div
            v-if="mentorOption === 'yes'"
            class="space-y-3 w-full max-w-[960px]"
          >
            <div
              v-if="loadingMentors"
              class="text-center py-8 text-muted-foreground"
            >
              <Loader />
              <p class="mt-2">Searching for mentors...</p>
            </div>
            <div
              v-else-if="mentors.length === 0"
              class="text-center py-10 bg-card border border-dashed border-border rounded-xl"
            >
              <div class="flex justify-center mb-3 text-muted-foreground/30">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17 21C17 19.4076 17 18.6114 16.7323 17.9858C16.4251 17.2681 15.8569 16.6999 15.1392 16.3927C14.5136 16.125 13.7174 16.125 12.125 16.125H11.875C10.2826 16.125 9.48637 16.125 8.86082 16.3927C8.14312 16.6999 7.57488 17.2681 7.26773 17.9858C7 18.6114 7 19.4076 7 21"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <path
                    d="M15 7C15 8.65685 13.6569 10 12 10C10.3431 10 9 8.65685 9 7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-dasharray="2 4"
                  />
                </svg>
              </div>
              <p class="text-sm font-medium text-foreground">
                No mentors found
              </p>
              <p class="text-xs text-muted-foreground mt-1 px-4">
                {{
                  mentorSearchQuery
                    ? `We couldn't find any mentors matching "${mentorSearchQuery}".`
                    : "There are currently no mentors available matching your criteria."
                }}
              </p>
            </div>
            <div
              v-else
              class="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar space-y-3"
            >
              <div
                v-for="mentor in mentors"
                :key="mentor.talent_id"
                :class="[
                  'border transition-all duration-500 overflow-hidden',
                  expandedMentorId === mentor.talent_id
                    ? 'rounded-[32px]'
                    : 'rounded-[100px]',
                  formData.mentor_id === mentor.talent_id
                    ? 'border-[#43CE33] bg-card'
                    : 'border-border bg-card',
                ]"
              >
                <!-- Collapsed Header -->
                <div
                  @click="toggleMentorExpansion(mentor.talent_id)"
                  class="px-6 py-5 flex items-center justify-between cursor-pointer"
                >
                  <div class="flex items-center gap-4">
                    <img
                      v-if="mentor.profile_picture"
                      :src="mentor.profile_picture"
                      alt=""
                      class="w-12 h-12 object-cover rounded-full"
                    />
                    <div
                      v-else
                      class="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-lg font-semibold text-muted-foreground"
                    >
                      {{ (mentor.first_name?.[0] || "U").toUpperCase() }}
                    </div>
                    <h3
                      class="text-[18px] md:text-[20px] font-medium text-foreground"
                    >
                      {{ mentor.first_name }} {{ mentor.last_name }}
                    </h3>
                  </div>
                  <div class="flex items-center gap-4">
                    <button
                      @click.stop="toggleMentorExpansion(mentor.talent_id)"
                      class="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <div
                        v-if="expandedMentorId === mentor.talent_id"
                        class="bg-foreground/10 px-[14px] py-[10px] rounded-full text-foreground"
                      >
                        <ArrowUp class="w-[18px] h-[18px]" />
                      </div>
                      <template v-else> View Profile </template>
                    </button>
                    <button
                      @click.stop="selectMentor(mentor.talent_id)"
                      :class="[
                        'px-6 py-2.5 rounded-full text-[14px] font-medium transition-all',
                        formData.mentor_id === mentor.talent_id
                          ? 'bg-muted text-foreground border border-foreground/10'
                          : 'bg-background hover:bg-muted/50 text-foreground border border-foreground/10',
                      ]"
                    >
                      {{
                        formData.mentor_id === mentor.talent_id
                          ? "Selected"
                          : "Select"
                      }}
                    </button>
                  </div>
                </div>

                <!-- Expanded Details -->
                <div
                  v-if="expandedMentorId === mentor.talent_id"
                  class="px-6 pb-6 animate-in slide-in-from-top-2 duration-300"
                >
                  <div class="space-y-6">
                    <!-- Bio & Description -->
                    <div class="space-y-3">
                      <div class="flex items-center gap-3">
                        <a
                          v-if="mentor.website"
                          :href="mentor.website"
                          target="_blank"
                          class="text-[#3b82f6] hover:underline text-sm flex items-center gap-1"
                        >
                          {{ mentor.website.replace(/^https?:\/\//, "") }}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-3 w-3"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path
                              d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                            ></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </a>
                        <a
                          v-if="mentor.email"
                          :href="'mailto:' + mentor.email"
                          class="hover:underline text-sm flex items-center gap-1"
                        >
                          {{ mentor.email }}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-3 w-3"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path
                              d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                            ></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </a>
                      </div>
                      <p
                        class="text-[15px] text-muted-foreground leading-relaxed"
                      >
                        {{
                          mentor.bio ||
                          "Experienced mentor specializing in " +
                            (mentor.skills?.[0] ||
                              mentor.expert_achievements?.[0]?.name ||
                              "various domains") +
                            ". Dedicated to helping learners reach their full potential."
                        }}
                        <strong v-if="mentor.sessions_completed"
                          >Completed
                          {{ mentor.sessions_completed }} Sessions.</strong
                        >
                      </p>
                    </div>

                    <!-- Skills & Expert Achievements -->
                    <div
                      v-if="
                        mentor.skills?.length ||
                        mentor.expert_achievements?.length
                      "
                      class="flex flex-wrap gap-2"
                    >
                      <!-- Dynamic Skills -->
                      <span
                        v-for="skill in mentor.skills"
                        :key="skill"
                        class="px-4 py-1.5 rounded-full bg-muted/50 text-[12px] text-muted-foreground border border-border/50"
                      >
                        {{ skill }}
                      </span>

                      <!-- Expert Achievements (New) -->
                      <span
                        v-for="achievement in mentor.expert_achievements"
                        :key="achievement.id"
                        class="px-4 py-1.5 rounded-full bg-primary/10 text-[12px] text-primary font-medium border border-primary/20 flex items-center gap-1.5"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="3"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        {{ achievement.name }}
                      </span>
                    </div>

                    <!-- Meta Info Grid -->
                    <div
                      class="grid grid-cols-3 gap-8 pt-4 border-t border-border/30"
                    >
                      <div class="space-y-1">
                        <p
                          class="text-[11px] uppercase tracking-wider text-muted-foreground/60 font-medium"
                        >
                          Joined
                        </p>
                        <p class="text-[14px] font-medium text-foreground">
                          {{
                            mentor.joined_at
                              ? new Date(mentor.joined_at).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )
                              : "May 5, 2020"
                          }}
                        </p>
                      </div>
                      <div class="space-y-1">
                        <p
                          class="text-[11px] uppercase tracking-wider text-muted-foreground/60 font-medium"
                        >
                          Earned Badge
                        </p>
                        <div class="flex items-center gap-1.5">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <path
                              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                            />
                          </svg>
                          <p class="text-[14px] font-medium text-foreground">
                            {{ mentor.badge_level || "Gold" }}
                          </p>
                        </div>
                      </div>
                      <div class="space-y-1">
                        <p
                          class="text-[11px] uppercase tracking-wider text-muted-foreground/60 font-medium"
                        >
                          Status
                        </p>
                        <p class="text-[14px] font-medium text-[#43CE33]">
                          Available
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 4: Learning Style -->
      <div v-if="currentStep === 3" class="space-y-8">
        <div
          class="space-y-2 md:space-y-3 text-[13px] md:text-sm text-muted-foreground mb-6 md:mb-8 text-left w-full"
        >
          <div>
            <p class="font-semibold text-foreground">
              What do you want to learn today?
            </p>
            <p class="text-muted-foreground">{{ formData.topic }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              What is your experience level?
            </p>
            <p class="text-muted-foreground">
              {{ getSkillLevelTitle(formData.target_skill_level) }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              How much time do you have to learn?
            </p>
            <p class="text-muted-foreground">{{ getDurationText() }}</p>
          </div>
        </div>

        <h1
          class="text-2xl md:text-3xl font-semibold text-foreground dark:text-white text-center"
        >
          What's your preferred learning style?
        </h1>

        <div class="flex flex-col items-center space-y-4">
          <div class="flex flex-wrap md:flex-nowrap justify-center gap-3 w-full max-w-[1000px]">
            <div
              v-for="style in learningStyles"
              :key="style.value"
              @click="selectLearningStyle(style.value)"
              :class="[
                'flex-1 min-w-[280px] md:min-w-0 p-5 rounded-[24px] border transition-all cursor-pointer flex flex-col items-center text-center space-y-2',
                formData.learning_style === style.value
                  ? 'border-[#43CE33] bg-[#43CE33]/5 dark:bg-[#43CE33]/10'
                  : 'border-border bg-card hover:border-foreground/20',
              ]"
            >
              <h3 class="text-base font-semibold text-foreground">
                {{ style.title }}
              </h3>
              <p class="text-[13px] text-muted-foreground leading-snug line-clamp-2">
                {{ style.description }}
              </p>
              <div
                v-if="formData.learning_style === style.value"
                class="w-6 h-6 rounded-full bg-[#43CE33] flex items-center justify-center text-white"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-3 mt-4">
            <button
              @click="previousStep"
              class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors text-white"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.73528 17.2206L0.25 8.73535M0.25 8.73535L8.73528 0.25007M0.25 8.73535H17.2206"
                  stroke="currentColor"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              @click="nextStep"
              :disabled="!canProceed"
              class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.48347 1.00007L17.9688 9.48535M17.9688 9.48535L9.48347 17.9706M17.9688 9.48535H0.998187"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      

      <!-- Step 6: Physical Workspace -->
      <div v-if="currentStep === 5" class="space-y-8">
        <div
          class="space-y-2 md:space-y-3 text-[13px] md:text-sm text-muted-foreground mb-6 md:mb-8 text-left w-full"
        >
          <div>
            <p class="font-semibold text-foreground">
              What do you want to learn today?
            </p>
            <p class="text-muted-foreground">{{ formData.topic }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              What is your experience level?
            </p>
            <p class="text-muted-foreground">
              {{ getSkillLevelTitle(formData.target_skill_level) }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              How much time do you have to learn?
            </p>
            <p class="text-muted-foreground">{{ getDurationText() }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Learning Style</p>
            <p class="text-muted-foreground">
              {{ getLearningStyleTitle(formData.learning_style) }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Selected Mentor</p>
            <p class="text-muted-foreground">
              {{
                selectedMentorData
                  ? selectedMentorData.first_name +
                    " " +
                    selectedMentorData.last_name
                  : "None"
              }}
            </p>
          </div>
        </div>

        <h1
          class="text-2xl md:text-3xl font-semibold text-foreground dark:text-white text-center"
        >
          Would you want a physical workspace?
        </h1>

        <div class="flex flex-col items-center space-y-6">
          <div class="flex flex-wrap justify-center gap-3">
            <button
              @click="selectWorkspaceOption('not_yet')"
              :class="[
                'px-6 py-3 rounded-full text-base font-medium transition-colors border',
                workspaceOption === 'not_yet'
                  ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                  : 'bg-card border-input text-muted-foreground hover:bg-muted dark:border-border',
              ]"
            >
              Not yet
            </button>
            <button
              @click="selectWorkspaceOption('yes')"
              :class="[
                'px-6 py-3 rounded-full text-base font-medium transition-colors border',
                workspaceOption === 'yes'
                  ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                  : 'bg-card border-input text-muted-foreground hover:bg-muted dark:border-border',
              ]"
            >
              Yes, choose a workspace
            </button>
          </div>

          <div class="w-full max-w-[960px] space-y-4">
            <div class="relative">
              <input
                v-model="workspaceSearchQuery"
                type="text"
                placeholder="Search for workspace"
                :disabled="workspaceOption !== 'yes'"
                class="w-full pl-6 pr-32 py-4 text-base border border-input dark:border-foreground/10 rounded-full focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground disabled:bg-muted disabled:cursor-not-allowed"
                @input="handleWorkspaceSearch"
              />
              <div
                class="absolute right-4 top-1/2 transform -translate-y-1/2 flex gap-2"
              >
                <button
                  @click="previousStep"
                  class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors text-white"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.73528 17.2206L0.25 8.73535M0.25 8.73535L8.73528 0.25007M0.25 8.73535H17.2206"
                      stroke="currentColor"
                      stroke-width="0.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  @click="nextStep"
                  class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors text-white"
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.48347 1.00007L17.9688 9.48535M17.9688 9.48535L9.48347 17.9706M17.9688 9.48535H0.998187"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div v-if="workspaceOption === 'yes'" class="space-y-4">
              <div
                v-if="loadingWorkspaces"
                class="text-center py-8 text-muted-foreground"
              >
                <Loader />
                <p class="mt-2 text-sm">Searching for workspaces...</p>
              </div>
              <div
                v-else-if="workspaces.length === 0"
                class="text-center py-10 bg-card border border-dashed border-border rounded-xl"
              >
                <p class="text-sm font-medium text-foreground">
                  No workspaces found
                </p>
                <p class="text-xs text-muted-foreground mt-1">
                  Try adjusting your search criteria.
                </p>
              </div>
              <div
                v-else
                class="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar space-y-3"
              >
                <div
                  v-for="workspace in workspaces"
                  :key="workspace.id"
                  :class="[
                    'border transition-all duration-500 overflow-hidden',
                    expandedWorkspaceId === workspace.id
                      ? 'rounded-[32px]'
                      : 'rounded-[100px]',
                    formData.workspace_id === workspace.id
                      ? 'border-[#43CE33] bg-card'
                      : 'border-border bg-card',
                  ]"
                >
                  <!-- Collapsed Header -->
                  <div
                    @click="toggleWorkspaceExpansion(workspace.id)"
                    class="px-6 py-5 flex items-center justify-between cursor-pointer"
                  >
                    <div class="space-y-1">
                      <h3
                        class="text-[18px] md:text-[20px] font-medium text-foreground"
                      >
                        {{ workspace.name }}
                      </h3>
                      <div
                        v-if="
                          workspace.address &&
                          expandedWorkspaceId === workspace.id
                        "
                        class="flex items-start gap-2 text-muted-foreground"
                      >
                        <MapPin class="h-4 w-4 mt-0.5" />
                        <span class="text-[14px]">{{ workspace.address }}</span>
                      </div>
                    </div>

                    <div class="flex items-center gap-4">
                      <button
                        @click="toggleWorkspaceExpansion(workspace.id)"
                        class="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <div
                          v-if="expandedWorkspaceId === workspace.id"
                          class="bg-foreground/10 px-[14px] py-[10px] rounded-full text-foreground"
                        >
                          <ArrowUp class="w-[18px] h-[18px]" />
                        </div>
                        <div v-else>More about this space</div>
                      </button>
                      <button
                        @click="selectWorkspace(workspace.id)"
                        :class="[
                          'px-6 py-2.5 rounded-full text-[14px] font-medium transition-all',
                          formData.workspace_id === workspace.id
                            ? 'bg-muted text-foreground border border-foreground/10'
                            : 'bg-background hover:bg-muted/50 text-foreground border border-foreground/10',
                        ]"
                      >
                        {{
                          formData.workspace_id === workspace.id
                            ? "Withdraw Request"
                            : "Request Access"
                        }}
                      </button>
                    </div>
                  </div>

                  <!-- Expanded Details -->
                  <div
                    v-if="expandedWorkspaceId === workspace.id"
                    class="px-6 pb-6 pt-2 animate-in slide-in-from-top-2 duration-300"
                  >
                    <div class="space-y-6">
                      <!-- Location & Description -->
                      <div class="space-y-3">
                        <p
                          class="text-[15px] text-muted-foreground leading-relaxed"
                        >
                          {{
                            workspace.description ||
                            "A professional workspace designed for collaboration and productivity."
                          }}
                        </p>
                      </div>

                      <!-- Amenities -->
                      <div
                        v-if="workspace.amenities?.length"
                        class="flex flex-wrap gap-2"
                      >
                        <span
                          v-for="amenity in workspace.amenities"
                          :key="amenity"
                          class="px-4 py-1.5 rounded-full bg-muted/50 text-[12px] text-muted-foreground border border-border/50"
                        >
                          {{ amenity }}
                        </span>
                      </div>

                      <!-- Meta Info Grid -->
                      <div
                        class="grid grid-cols-3 gap-8 pt-4 border-t border-border/30"
                      >
                        <div class="space-y-1">
                          <p
                            class="text-[11px] uppercase tracking-wider text-muted-foreground/60 font-medium"
                          >
                            Opening Hours
                          </p>
                          <p class="text-[14px] font-semibold text-foreground">
                            {{
                              workspace.operating_hours?.monday ||
                              "9:00AM to 5:00PM"
                            }}
                          </p>
                        </div>
                        <div class="space-y-1">
                          <p
                            class="text-[11px] uppercase tracking-wider text-muted-foreground/60 font-medium"
                          >
                            Pricing
                          </p>
                          <p class="text-[14px] font-semibold text-foreground">
                            {{
                              workspace.pricing?.is_free
                                ? "Free"
                                : (workspace.pricing?.currency || "NGN") +
                                  " " +
                                  (workspace.pricing?.price_per_day ||
                                    "6,000") +
                                  " Daily"
                            }}
                          </p>
                        </div>
                        <div class="space-y-1">
                          <p
                            class="text-[11px] uppercase tracking-wider text-muted-foreground/60 font-medium"
                          >
                            Rating
                          </p>
                          <div class="flex items-center gap-1.5">
                            <p
                              class="text-[14px] font-semibold text-foreground"
                            >
                              4.9
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 7: Resource Selection -->
      <div v-if="currentStep === 6" class="space-y-8">
        <div
          class="space-y-2 md:space-y-3 text-[13px] md:text-sm text-muted-foreground mb-6 md:mb-8 text-left w-full"
        >
          <div>
            <p class="font-semibold text-foreground">
              What do you want to learn today?
            </p>
            <p class="text-muted-foreground">{{ formData.topic }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              What is your experience level?
            </p>
            <p class="text-muted-foreground">
              {{ getSkillLevelTitle(formData.target_skill_level) }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              How much time do you have to learn?
            </p>
            <p class="text-muted-foreground">{{ getDurationText() }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Learning Style</p>
            <p class="text-muted-foreground">
              {{ getLearningStyleTitle(formData.learning_style) }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Selected Mentor</p>
            <p class="text-muted-foreground">
              {{
                selectedMentorData
                  ? selectedMentorData.first_name +
                    " " +
                    selectedMentorData.last_name
                  : "None"
              }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Selected Workspace</p>
            <p class="text-muted-foreground">
              {{ selectedWorkspaceData ? selectedWorkspaceData.name : "None" }}
            </p>
          </div>
        </div>

        <div class="flex flex-col items-center space-y-8">
          <!-- Segmented Control (Tabs) -->
          <div class="flex flex-wrap justify-center gap-4">
            <button
              v-for="option in resourceOptions"
              :key="option.value"
              @click="formData.resource_type = option.value"
              :disabled="option.value === 'high_quality' && !isPaidUser"
              :class="[
                'px-[28px] py-[10px] rounded-full text-[15px] font-medium transition-all duration-200 border cursor-pointer',
                formData.resource_type === option.value
                  ? 'bg-[#333333] text-white border-[#333333]'
                  : option.value === 'high_quality' && !isPaidUser
                  ? 'bg-[#F9F9F9] text-[#A3A3A3] border-transparent cursor-not-allowed'
                  : 'bg-white text-[#828282] border-[#E5E5E5] hover:border-[#CCCCCC]',
              ]"
            >
              {{ option.title }}
            </button>
          </div>

          <!-- Description Row -->
          <div
            v-if="formData.resource_type"
            class="flex items-center justify-center gap-2 max-w-2xl px-4 animate-in fade-in duration-300 min-h-[24px]"
          >
            <div
              class="bg-[#A3A3A3] rounded-full shrink-0 flex items-center justify-center text-white"
              style="width: 16px; height: 16px"
            >
              <span class="text-[10px] font-bold leading-none italic font-serif"
                >i</span
              >
            </div>
            <p class="text-[14px] text-[#A3A3A3] text-center">
              {{
                formData.resource_type
                  ? resourceOptions.find(
                      (o) => o.value === formData.resource_type,
                    )?.description
                  : ""
              }}
            </p>
          </div>
          <!-- Spacer for when no description to prevent jump -->
          <div v-else class="min-h-[24px]"></div>

          <!-- Main Input Pill Section -->
          <div class="w-full max-w-[800px] flex flex-col items-center">
            <div
              class="relative w-full border border-[#F0F0F0] bg-white transition-all duration-300 flex items-center"
              :class="[
                formData.resource_type === 'personal' &&
                formData.resource_files.length
                  ? 'rounded-[32px] p-[16px] pr-[110px] min-h-[80px]'
                  : 'rounded-full h-[72px] pl-[28px] pr-[110px]',
              ]"
              style="box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.03)"
            >
              <!-- Initial Placeholder -->
              <span
                v-if="!formData.resource_type"
                class="text-[17px] text-[#A3A3A3] font-normal"
              >
                Select your choice of learning resource
              </span>

              <!-- High-Quality (Unpaid) -->
              <span
                v-else-if="
                  formData.resource_type === 'high_quality' && !isPaidUser
                "
                class="text-[17px] text-[#A3A3A3] font-normal"
              >
                High-Quality
              </span>

              <!-- Free / High-Quality (Paid) -->
              <span
                v-else-if="formData.resource_type !== 'personal'"
                class="text-[17px] text-[#333333] font-medium"
              >
                {{
                  resourceOptions.find(
                    (o) => o.value === formData.resource_type,
                  )?.title
                }}
              </span>

              <!-- Personal Input Area -->
              <div v-else class="flex-1 w-full flex flex-wrap gap-2 items-center">
                <!-- Empty State -->
                <div
                  v-if="!formData.resource_files.length"
                  class="flex items-center gap-[10px] h-full relative cursor-pointer group w-full"
                >
                  <Paperclip class="w-[20px] h-[20px] text-[#A3A3A3]" />
                  <span
                    class="text-[17px] text-[#A3A3A3] font-normal truncate group-hover:text-[#828282] transition-colors"
                  >
                    Attach learning resources (.pdf, .docx)
                  </span>
                  <input
                    type="file"
                    multiple
                    class="absolute inset-0 opacity-0 cursor-pointer"
                    @change="handleFileUpload"
                  />
                </div>

                <!-- Filled State (File Chips) -->
                <template v-else>
                  <div
                    v-for="(file, index) in formData.resource_files"
                    :key="index"
                    class="flex items-center gap-[8px] px-[16px] py-[8px] rounded-full border text-[14px] tracking-wide transition-all duration-200"
                    :class="[
                      file.status === 'uploading' ? 'bg-[#F9F9F9] border-[#E5E5E5] text-[#A3A3A3] animate-pulse-subtle' :
                      file.status === 'error' ? 'bg-red-50 border-red-200 text-red-500' :
                      'bg-[#F9F9F9] border-[#E5E5E5] text-[#A3A3A3] hover:border-[#CCCCCC]'
                    ]"
                  >
                    <div v-if="file.status === 'uploading'" class="h-3 w-3 border-[1.5px] border-[#A3A3A3] border-t-transparent rounded-full animate-spin"></div>
                    <span class="truncate max-w-[150px]">{{ file.name }}</span>
                    <button
                      @click="removeFile(index)"
                      class="hover:text-black transition-colors shrink-0 outline-none ml-1"
                    >
                      <X class="w-3.5 h-3.5" />
                    </button>
                  </div>
                  
                  <!-- Compact Add button -->
                  <div
                    class="relative flex items-center justify-center w-[36px] h-[36px] rounded-full border border-dashed border-[#E5E5E5] text-[#A3A3A3] hover:text-[#828282] hover:border-[#cccccc] hover:bg-muted/30 cursor-pointer transition-all shrink-0"
                    title="Add more files"
                  >
                    <Plus class="w-4 h-4" />
                    <input
                      type="file"
                      multiple
                      class="absolute inset-0 opacity-0 cursor-pointer"
                      @change="handleFileUpload"
                    />
                  </div>
                </template>
              </div>

              <!-- Nav Arrows (Inside Pill) -->
              <div
                class="absolute right-[12px] top-1/2 -translate-y-1/2 flex items-center gap-[10px]"
              >
                <button
                  @click="previousStep"
                  class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white shrink-0"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.73528 17.2206L0.25 8.73535M0.25 8.73535L8.73528 0.25007M0.25 8.73535H17.2206"
                      stroke="currentColor"
                      stroke-width="0.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  @click="nextStep"
                  :disabled="!canProceed"
                  class="w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white shrink-0"
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.48347 1.00007L17.9688 9.48535M17.9688 9.48535L9.48347 17.9706M17.9688 9.48535H0.998187"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 8: Additional Information -->
      <div v-if="currentStep === 7" class="space-y-8">
        <div
          class="space-y-2 md:space-y-3 text-[13px] md:text-sm text-muted-foreground mb-6 md:mb-8 text-left w-full"
        >
          <div>
            <p class="font-semibold text-foreground">
              What do you want to learn today?
            </p>
            <p class="text-muted-foreground">{{ formData.topic }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              What is your experience level?
            </p>
            <p class="text-muted-foreground">
              {{ getSkillLevelTitle(formData.target_skill_level) }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">
              How much time do you have to learn?
            </p>
            <p class="text-muted-foreground">{{ getDurationText() }}</p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Learning Style</p>
            <p class="text-muted-foreground">
              {{ getLearningStyleTitle(formData.learning_style) }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Selected Mentor</p>
            <p class="text-muted-foreground">
              {{
                selectedMentorData
                  ? selectedMentorData.first_name +
                    " " +
                    selectedMentorData.last_name
                  : "None"
              }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Selected Workspace</p>
            <p class="text-muted-foreground">
              {{ selectedWorkspaceData ? selectedWorkspaceData.name : "None" }}
            </p>
          </div>
          <div>
            <p class="font-semibold text-foreground">Resource Type</p>
            <p class="text-muted-foreground">
              {{
                formData.resource_type
                  ? resourceOptions.find(
                      (o) => o.value === formData.resource_type,
                    )?.title
                  : "None"
              }}
            </p>
          </div>
        </div>

        <h1
          class="text-2xl md:text-3xl font-semibold text-foreground dark:text-white text-center"
        >
          Any additional information?
        </h1>

        <div class="relative flex items-center h-[56px] w-full">
          <textarea
            v-model="formData.additional_information"
            placeholder="Please provide any additional information you would like us to consider when generating the plan."
            class="w-full pl-6 pr-32 py-4 h-[80px] text-base border border-input dark:border-foreground/10 rounded-[32px] focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground resize-none"
            @keydown.enter.prevent="handleCreateGoalClick"
          ></textarea>
          <div
            class="absolute right-4 top-1/2 transform -translate-y-1/2 flex gap-2"
          >
            <button
              @click="previousStep"
              class="group w-12 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors text-white"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.73528 17.2206L0.25 8.73535M0.25 8.73535L8.73528 0.25007M0.25 8.73535H17.2206"
                  stroke="currentColor"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              @click="handleCreateGoalClick"
              :disabled="isCreating"
              class="w-10 h-10 rounded-full bg-black dark:bg-muted/50 hover:bg-gray-900 dark:hover:bg-muted/70 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              <svg
                v-if="!isCreating"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <svg
                v-else
                class="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success state (already present) -->
    <div
      v-if="isCreating && !showCreditModal"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm"
    >
      <div class="text-center">
        <Loader size="lg" />
        <p class="mt-4 text-lg font-medium text-foreground">
          Creating your learning goal...
        </p>
      </div>
    </div>

    <!-- Credit Confirmation Modal -->
    <GoalCreditModal
      :is-open="showCreditModal"
      :available-credits="availableCredits"
      :cost="modalCost"
      :is-processing="isCreating"
      @close="showCreditModal = false"
      @continue="confirmCreateGoal"
      @top-up="redirectToPricing"
    />
  </div>
</template>

<script>
import BackButton from "@/components/BackButton.vue";
import apiClient from "@/axios";
import Loader from "@/components/ui/Loader.vue";
import GoalCreditModal from "@/components/modals/GoalCreditModal.vue";
import { ArrowUp, MapPin, Paperclip, X, Plus } from "lucide-vue-next";

export default {
  name: "CreateLearningGoalComponent",
  components: {
    BackButton,
    Loader,
    GoalCreditModal,
    ArrowUp,
    MapPin,
    Paperclip,
    X,
    Plus,
  },
  data() {
    return {
      currentStep: 0,
      isCreating: false,
      customSkillLevel: "",
      customDuration: null,
      workspaceSearchQuery: "",
      workspaceSearchTimeout: null,
      workspaceOption: "not_yet",
      loadingWorkspaces: false,
      expandedWorkspaceId: null,
      expandedMentorId: null,
      selectedMentorData: null,
      selectedWorkspaceData: null,
      mentorSearchQuery: "",
      mentorSearchTimeout: null,
      mentorOption: "not_yet",
      loadingMentors: false,
      showCreditModal: false,
      availableCredits: 0,
      modalCost: 10,
      uploadingResources: false,
      formData: {
        topic: "",
        target_skill_level: "",
        estimated_duration_weeks: null,
        additional_information: "",
        learning_style: "video-first",
        mentor_id: null,
        workspace_id: null,
        resource_type: "online", // UI Selection
        resource_files: [], // For UI display { name, status, id }
        personal_resources: [], // For Backend { filename, url, full_text, etc. }
      },
      skillLevels: [
        { title: "Any", value: "any" },
        { title: "Beginner", value: "beginner" },
        { title: "Intermediate", value: "intermediate" },
        { title: "Advanced", value: "advanced" },
      ],
      durationOptions: [
        { title: "Any", value: null },
        { title: "4 weeks", value: 4 },
        { title: "8 weeks", value: 8 },
        { title: "12 weeks", value: 12 },
      ],
      resourceOptions: [
        {
          title: "Free",
          value: "online",
          description:
            "This option gives you access to regular online materials that relate to your learning goal only",
        },
        {
          title: "High-Quality",
          value: "verified",
          description:
            "This option gives you access to high-quality pre-reviewed resources. Only available to paid users",
        },
        {
          title: "Personal",
          value: "personal",
          description:
            "This option allows you upload as many materials as you want. Suitable for narrowed learnings.",
        },
      ],
      learningStyles: [
        {
          value: "video-first",
          title: "Video-First",
          description:
            "Learn best by watching explanations, demonstrations, and walkthroughs.",
        },
        {
          value: "reading-first",
          title: "Reading-First",
          description:
            "Prefer articles, documentation, and written guides that you can move through at your own pace.",
        },
        // {
        //   value: "interactive",
        //   title: "Interactive",
        //   description:
        //     "Enjoy interactive tools, quizzes, simulations, and learning by doing rather than consuming content.",
        // },
        {
          value: "balanced",
          title: "Balanced (Mix)",
          description:
            "A comprehensive mix of video tutorials and deep-dive reading materials for an all-around learning experience.",
        },
      ],
      isPaidUser: false, // Will be fetched from subscription
      mentors: [],
      workspaces: [],
      pollingInterval: null,
    };
  },
  computed: {
    canProceed() {
      switch (this.currentStep) {
        case 0:
          return this.formData.topic?.trim().length > 0;
        case 1:
          return (
            this.formData.target_skill_level || this.customSkillLevel?.trim()
          );
        case 2:
          return (
            this.formData.estimated_duration_weeks !== null ||
            this.customDuration > 0
          );
        case 3:
          return !!this.formData.learning_style;
        case 4:
          return true; // Mentor is optional
        case 5:
          return true; // Hubs is optional
        case 6:
          if (!this.formData.resource_type) return false;
          if (this.formData.resource_type === "high_quality")
            return this.isPaidUser;
          if (this.formData.resource_type === "custom") {
            const hasFiles = this.formData.resource_files.length > 0;
            const allUploaded = this.formData.resource_files.every(
              (f) => f.status === "success",
            );
            return hasFiles && allUploaded;
          }
          return true; // Free is always okay
        case 7:
          return true; // Additional information is optional
        default:
          return false;
      }
    },
    selectedMentor() {
      if (!this.formData.mentor_id) return null;
      return (
        this.mentors.find((m) => m.talent_id === this.formData.mentor_id) ||
        null
      );
    },
    selectedWorkspace() {
      if (!this.formData.workspace_id) return null;
      return (
        this.workspaces.find((w) => w.id === this.formData.workspace_id) || null
      );
    },
  },
  async created() {
    await this.fetchSubscription();

    if (this.$route.query.topic) {
      this.formData.topic = this.$route.query.topic;
    }
    if (this.$route.query.level) {
      this.formData.target_skill_level = this.$route.query.level;
    }
    if (this.$route.query.skip_to_summary === 'true') {
      if (!this.formData.learning_style) this.formData.learning_style = 'video-first';
      if (!this.formData.learning_tone) this.formData.learning_tone = 'professional';
      if (!this.formData.estimated_duration_weeks) this.formData.estimated_duration_weeks = 4;
      if (!this.formData.weekly_time_commitment) this.formData.weekly_time_commitment = 5;
      this.currentStep = 7;
    }
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async fetchSubscription() {
      try {
        const response = await apiClient.get(
          "/web/subscriptions/my-subscription",
        );
        if (response.data && response.data.data) {
          this.isPaidUser = !!response.data.data.id; // Or specific status check
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
      }
    },

    async checkGoalStatus(goalId) {
      if (!goalId) return;

      try {
        const response = await apiClient.get(`/web/learning-goals/${goalId}`);
        const goal = response.data;
        const status = (goal?.status || "").toLowerCase();

        if (status === "active") {
          this.stopPolling();
          this.$router.push(`/learning-goals/${goalId}`);
        } else if (status === "failed") {
          this.stopPolling();
          this.isCreating = false;
          this.$router.push(`/learning-goals/${goalId}`);

          // this.$toast?.error(
          //   "Generation Failed",
          //   "We encountered an issue creating your learning path. Please try again or simplify your request.",
          // );
        }
      } catch (error) {
        console.error("Error checking goal status during polling:", error);
      }
    },

    startPolling(goalId) {
      if (this.pollingInterval || !goalId) return;
      this.pollingInterval = setInterval(() => {
        this.checkGoalStatus(goalId);
      }, 3000);
    },

    selectLearningStyle(style) {
      this.formData.learning_style = style;
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    selectSkillLevel(level) {
      this.formData.target_skill_level = level;
      this.customSkillLevel = "";
    },

    selectDuration(weeks) {
      this.formData.estimated_duration_weeks = weeks;
      this.customDuration = null;
    },

    selectCustomDuration() {
      if (this.customDuration && this.customDuration > 0) {
        this.formData.estimated_duration_weeks = this.customDuration;
      }
    },

    selectMentorOption(option) {
      this.mentorOption = option;
      if (option === "not_yet") {
        this.formData.mentor_id = null;
        this.selectedMentorData = null;
        this.mentorSearchQuery = "";
        this.mentors = [];
      } else {
        this.fetchMentors();
      }
    },

    selectMentor(mentorId) {
      if (this.formData.mentor_id === mentorId) {
        this.formData.mentor_id = null;
        this.selectedMentorData = null;
      } else {
        this.formData.mentor_id = mentorId;
        this.selectedMentorData =
          this.mentors.find((m) => m.talent_id === mentorId) || null;
      }
    },

    toggleMentorExpansion(mentorId) {
      if (this.expandedMentorId === mentorId) {
        this.expandedMentorId = null;
      } else {
        this.expandedMentorId = mentorId;
      }
    },

    handleMentorSearch() {
      clearTimeout(this.mentorSearchTimeout);
      this.mentorSearchTimeout = setTimeout(() => {
        this.fetchMentors();
      }, 500);
    },

    async fetchMentors() {
      try {
        this.loadingMentors = true;
        const response = await apiClient.get("/general/mentors", {
          params: {
            search: this.mentorSearchQuery,
            sort_by: "newest",
            limit: 10,
            has_expert_achievement: true,
          },
        });
        this.mentors = response.data.data || [];
      } catch (error) {
        console.error("Error fetching mentors:", error);
        this.mentors = [];
      } finally {
        this.loadingMentors = false;
      }
    },

    getMentorName(mentorId) {
      const mentor = this.mentors.find((m) => m.talent_id === mentorId);
      return mentor
        ? `${mentor.first_name} ${mentor.last_name}`
        : "Unknown Mentor";
    },

    getSkillLevelTitle(value) {
      if (this.customSkillLevel) return this.customSkillLevel;
      const level = this.skillLevels.find((level) => level.value === value);
      return level ? level.title : value;
    },

    getLearningStyleTitle(value) {
      const style = this.learningStyles.find((s) => s.value === value);
      return style ? style.title : value;
    },

    getDurationText() {
      if (!this.formData.estimated_duration_weeks) return "Any";
      const weeks = this.formData.estimated_duration_weeks;
      return weeks === 1 ? "1 week" : `${weeks} weeks`;
    },

    async handleFileUpload(event) {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      for (const file of files) {
        const fileId = Math.random().toString(36).substring(7);
        const fileObj = {
          id: fileId,
          name: file.name,
          status: "uploading",
        };
        
        this.formData.resource_files.push(fileObj);

        try {
          const uploadData = new FormData();
          uploadData.append("file", file);
          
          const response = await apiClient.post("/web/learning-goals/upload-resource", uploadData, {
            headers: { "Content-Type": "multipart/form-data" }
          });

          // Find the exact object in the array to ensure reactivity
          const currentFileIdx = this.formData.resource_files.findIndex(f => f.id === fileId);
          if (currentFileIdx === -1) continue;

          if (response.data && response.data.success) {
            this.formData.resource_type = "personal";
            this.formData.resource_files[currentFileIdx].status = "success";
            this.formData.personal_resources.push({
              id: fileId,
              filename: response.data.filename,
              file_key: response.data.file_key,
              url: response.data.url,
              full_text: response.data.full_text
            });
            this.$toast.success("Success", `Processed ${file.name}`);
          } else {
            this.formData.resource_files[currentFileIdx].status = "error";
            this.$toast.error("Format Error", `Could not extract text from ${file.name}`);
          }
        } catch (error) {
          console.error("Error uploading file:", error);
          const currentFileIdx = this.formData.resource_files.findIndex(f => f.id === fileId);
          if (currentFileIdx !== -1) {
            this.formData.resource_files[currentFileIdx].status = "error";
          }
          this.$toast.error("Upload Failed", `Could not upload ${file.name}`);
        }
      }
      
      // Force array reactivity update
      this.formData.resource_files = [...this.formData.resource_files];
      
      // Reset input value
      event.target.value = "";
    },

    removeFile(index) {
      const removedFile = this.formData.resource_files[index];
      this.formData.resource_files.splice(index, 1);
      
      // Also remove from personal_resources list sent to backend
      const personalIdx = this.formData.personal_resources.findIndex(r => r.id === removedFile.id);
      if (personalIdx !== -1) {
        this.formData.personal_resources.splice(personalIdx, 1);
      }
    },

    selectWorkspaceOption(option) {
      this.workspaceOption = option;
      if (option === "not_yet") {
        this.formData.workspace_id = null;
        this.selectedWorkspaceData = null;
        this.workspaceSearchQuery = "";
        this.workspaces = [];
      } else {
        this.fetchWorkspaces();
      }
    },

    selectWorkspace(workspaceId) {
      if (this.formData.workspace_id === workspaceId) {
        this.formData.workspace_id = null;
        this.selectedWorkspaceData = null;
      } else {
        this.formData.workspace_id = workspaceId;
        this.selectedWorkspaceData =
          this.workspaces.find((w) => w.id === workspaceId) || null;
      }
    },

    toggleWorkspaceExpansion(workspaceId) {
      if (this.expandedWorkspaceId === workspaceId) {
        this.expandedWorkspaceId = null;
      } else {
        this.expandedWorkspaceId = workspaceId;
      }
    },

    handleWorkspaceSearch() {
      clearTimeout(this.workspaceSearchTimeout);
      this.workspaceSearchTimeout = setTimeout(() => {
        this.fetchWorkspaces();
      }, 500);
    },

    async fetchWorkspaces() {
      try {
        this.loadingWorkspaces = true;
        const response = await apiClient.get("/web/workspace", {
          params: {
            search: this.workspaceSearchQuery,
          },
        });
        this.workspaces = response.data?.data
          ? response.data.data
          : response.data || [];
      } catch (error) {
        console.error("Error fetching workspaces:", error);
        this.workspaces = [];
      } finally {
        this.loadingWorkspaces = false;
      }
    },

    nextStep() {
      if (!this.canProceed) return;

      // Apply custom values if entered
      if (this.currentStep === 1 && this.customSkillLevel?.trim()) {
        this.formData.target_skill_level = this.customSkillLevel.trim();
      }
      if (this.currentStep === 2 && this.customDuration > 0) {
        this.formData.estimated_duration_weeks = this.customDuration;
      }

      if (this.currentStep < 7) {
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    goBack() {
      if (this.currentStep > 0) {
        this.previousStep();
      } else {
        this.$router.back();
      }
    },

    async createLearningGoal() {
      try {
        this.isCreating = true;

        const payload = {
          topic: this.formData.topic,
          target_skill_level: this.formData.target_skill_level || "any",
          estimated_duration_weeks: this.formData.estimated_duration_weeks || 4,
          additional_information: this.formData.additional_information || "",
          learning_style: this.formData.learning_style,
          mentor_id: this.formData.mentor_id || null,
          workspace_id: this.formData.workspace_id || null,
          resourceOrigin: this.formData.resource_type, 
          personal_resources: this.formData.resource_type === 'personal' ? this.formData.personal_resources : []
        };

        const response = await apiClient.post(
          "/web/learning-goals/create",
          payload
        );

        if (response.status === 200 || response.status === 201) {
          const newGoalId =
            response.data.project_id ||
            response.data.data?.project_id ||
            response.data.id;

          this.$toast.success(
            "Generation Started",
            "Your learning goal is being created. Please wait...",
          );

          if (newGoalId) {
            this.startPolling(newGoalId);
          } else {
            this.isCreating = false;
            this.$router.push("/learning-goals");
          }
          return;
        }
      } catch (error) {
        console.error("Error creating learning goal:", error);

        let errorMessage = "Failed to create learning goal. Please try again.";
        let errorTitle = "Creation Failed";

        if (error.response) {
          errorMessage = error.response.data?.message || errorMessage;

          if (error.response.status === 504 || error.response.status === 408) {
            errorMessage =
              "The server took too long to respond. The goal might have been created in the background. Please check your dashboard.";
            errorTitle = "Request Timed Out";
            this.$router.push("/learning-goals");
          } else if (errorMessage.toLowerCase().includes("exist")) {
            errorTitle = "Goal Already Exists";
            errorMessage =
              "A learning goal with this topic may have been created already. Please check your dashboard.";
          }
        } else if (error.code === "ECONNABORTED" || !error.response) {
          errorMessage =
            "The connection timed out. The goal might have been created in the background. Please check your dashboard.";
          errorTitle = "Connection Issue";
        }

        this.$toast.error(errorTitle, errorMessage);
        // Only reset isCreating on error, not on success
        // This keeps the loading view visible during navigation
        this.isCreating = false;
      }
    },

    async handleCreateGoalClick() {
      try {
        const response = await apiClient.get(
          "/web/subscriptions/my-subscription",
        );
        const subData = response.data.data;
        this.availableCredits = subData?.current_credits || 0;
        this.showCreditModal = true;
      } catch (error) {
        console.error("Error fetching credits:", error);
        this.availableCredits = 0;
        this.showCreditModal = true;
      }
    },

    confirmCreateGoal() {
      this.showCreditModal = false;
      this.createLearningGoal();
    },

    redirectToPricing() {
      this.showCreditModal = false;
      this.$router.push("/pricing");
    },

    cancelCreation() {
      // Reset the creation state
      this.isCreating = false;
      this.stopPolling();
      // Optionally, you can redirect back to the learning goals page
      this.$router.push("/learning-goals");
    },
  },
};
</script>

<style scoped>
/* Custom animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.space-y-8 > * {
  animation: fadeIn 0.3s ease-out;
}

/* Custom Scrollbar Styles */
@keyframes pulse-subtle {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 0.9; }
}

.animate-pulse-subtle {
  animation: pulse-subtle 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #1e293b;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}
</style>
